/**
 * Leasing Leads — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/leasing-leads.schemas.ts`
 * imports and re-exports these to register ~10 lead-family routes, and any frontend client
 * should derive its Lead response types from the same objects rather than hand-declaring
 * them. See packages/api-types/README.md.
 *
 * Covers every route returning a `LeadResponse` (as `lead` or within `items`):
 *   GET    /landlord/api/listings/:id/leads
 *   POST   /landlord/api/listings/:id/leads
 *   GET    /landlord/api/leads
 *   PATCH  /landlord/api/leads/:id
 *   POST   /landlord/api/leads/:id/advance
 *   POST   /landlord/api/leads/:id/decide
 *   POST   /landlord/api/leads/:id/reopen
 *   POST   /landlord/api/leads/:id/send-initial-approval
 *   POST   /landlord/api/leads/:id/legal-hold
 *
 * Also covers (added in the #922 migration sweep) the lead detail sub-schemas, request
 * bodies, and the cross-listing query:
 *   GET  /landlord/api/leads/:id                        (case/messages/events sub-objects)
 *   POST /landlord/api/listings/:id/leads                (CreateLeadBodySchema)
 *   PATCH /landlord/api/leads/:id                        (UpdateLeadBodySchema)
 *   GET  /landlord/api/leads                             (ListAllLeadsQuerySchema)
 *   POST /landlord/api/leads/:id/messages                (SendReplyAttachmentSchema)
 *   POST /landlord/api/leads/:id/decide                  (DecideLeadBodySchema)
 *   POST /landlord/api/leads/:id/rejection-notice         (RejectionNoticeBodySchema)
 */
import { z } from 'zod';
import { JurisdictionCoverageSchema } from '../lease-generation/jurisdiction-coverage.js';
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
const uuid = z.uuid();
const dateString = z.string(); // ISO 8601 string
// Lead rejection basis — must match LeadRejectionBasis in @resida/database
export const LeadRejectionBasisSchema = z.enum([
    'procedural_listing_closed',
    'procedural_other_applicant',
    'substantive_consumer_report',
    'substantive_employment',
    'substantive_rental_history',
    'substantive_other',
]);
// Lead enums — must match LeadStage and LeadDecision in @resida/database
export const LeadStageSchema = z.enum([
    'inquiring',
    'showing',
    'applying',
    'verifying',
    'deciding',
    'closed',
]);
export const LeadDecisionSchema = z.enum([
    'approved_lease_started',
    'withdrawn',
    'rejected',
    'no_decision',
]);
// ---------------------------------------------------------------------------
// Lead response schema
// ---------------------------------------------------------------------------
export const LeadResponseSchema = z.object({
    id: uuid,
    listingId: uuid,
    landlordScopeId: uuid,
    prospectName: z.string().nullable(),
    prospectEmail: z.email(),
    prospectPhone: z.string().nullable(),
    stage: LeadStageSchema,
    decision: LeadDecisionSchema.nullable(),
    decisionNote: z.string().nullable(),
    decidedAt: dateString.nullable(),
    decidedByUserId: uuid.nullable(),
    claimedMonthlyIncomeCents: z.number().int().nullable(),
    claimedHouseholdSize: z.number().int().nullable(),
    claimedPets: z.number().int().nullable(),
    claimedMoveInDate: dateString.nullable(),
    claimedNotes: z.string().nullable(),
    source: z.string().nullable(),
    legalHold: z.boolean(),
    dataDisposedAt: dateString.nullable(),
    createdAt: dateString,
    updatedAt: dateString,
    lastActivityAt: dateString,
    // Location fields
    propertyAddress: z.string().nullable(),
    unitLabel: z.string().nullable(),
    // Read state
    hasUnread: z.boolean(),
    // Rejection fields (Phase 3 — landlord compliance)
    rejectionBasis: LeadRejectionBasisSchema.nullable(),
    rejectionNoticeSentAt: dateString.nullable(),
    // Initial approval fields (BR-35/BR-36)
    initialApprovalSentAt: dateString.nullable(),
    initialApprovalSentByUserId: uuid.nullable(),
});
/**
 * DC-51 — present on the lead DETAIL response only (the list/create/advance routes do not
 * compute it): a current paid period on the unit OR the landlord's agent add-on is active.
 */
export const LeadAgentAvailabilitySchema = z.object({
    leasingAgentAvailable: z.boolean(),
    /**
     * BR-28 / DC-17 — the reference agent is switched on for THIS LEAD's landlord scope (the scope the
     * create-reference-check gate resolves against), not the caller's own default policy: a listing
     * delegate with `lead.manage` sees the owner's switch. False while off, with no row, or when the
     * switch could not be read (the server gate stays authoritative).
     */
    referenceAgentEnabled: z.boolean(),
});
// ---------------------------------------------------------------------------
// POST /landlord/api/leads/:id/send-initial-approval — send initial approval (BR-35/BR-36)
// ---------------------------------------------------------------------------
export const SendInitialApprovalResponseSchema = z.object({
    lead: LeadResponseSchema,
    applicantsNotified: z.number().int(),
});
// ---------------------------------------------------------------------------
// Lead detail sub-schemas — case, channel message, case event (GET /leads/:id)
// ---------------------------------------------------------------------------
export const CaseResponseSchema = z.object({
    id: uuid,
    agentKindId: uuid,
    ownerActorType: z.string(),
    ownerActorId: z.string().nullable(),
    landlordScopeId: uuid,
    closedAt: dateString.nullable(),
    createdAt: dateString,
    updatedAt: dateString,
});
export const ChannelMessageResponseSchema = z.object({
    id: uuid,
    channelId: uuid,
    direction: z.string(),
    externalRef: z.string().nullable(),
    content: z.string(),
    routingDecision: z.string(),
    routedToCaseId: uuid.nullable(),
    routedAt: dateString.nullable(),
    status: z.string(),
    createdAt: dateString,
    /** CaseEvent the message is tied to (feedback "Flag this" anchor, BR-AGENT-FB-1); null when none. */
    caseEventId: uuid.nullable(),
    /** That event's actorKind — lets the UI offer feedback on agent-authored messages only; null when none. */
    authorKind: z.string().nullable(),
});
export const CaseEventResponseSchema = z.object({
    id: uuid,
    caseId: uuid,
    eventType: z.string(),
    actorKind: z.string(),
    actorId: z.string().nullable(),
    payload: z.unknown(),
    result: z.unknown(),
    createdAt: dateString,
});
// ---------------------------------------------------------------------------
// GET /landlord/api/leads/:id — lead detail
// ---------------------------------------------------------------------------
export const LeadDetailResponseSchema = LeadResponseSchema.extend(LeadAgentAvailabilitySchema.shape).extend({
    case: CaseResponseSchema,
    messages: z.array(ChannelMessageResponseSchema),
    events: z.array(CaseEventResponseSchema),
    adverseActionNoticeRequired: z.boolean(),
    // Unit's US state code — the client derives the property's display timezone from this.
    unitState: z.string().nullable(),
    // What the rules library knows about the unit's jurisdiction; null when the constraints
    // lookup failed (same fail-open path as adverseActionNoticeRequired).
    jurisdictionCoverage: JurisdictionCoverageSchema.nullable(),
});
// ---------------------------------------------------------------------------
// POST /landlord/api/listings/:id/leads — manual lead creation (request body)
// ---------------------------------------------------------------------------
export const CreateLeadBodySchema = z.object({
    prospectEmail: z.email(),
    prospectName: z.string().nullable().optional(),
    prospectPhone: z.string().nullable().optional(),
    source: z.string().max(100).nullable().optional(),
    claimedMonthlyIncomeCents: z.number().int().nonnegative().nullable().optional(),
    claimedHouseholdSize: z.number().int().positive().nullable().optional(),
    claimedPets: z.number().int().nonnegative().nullable().optional(),
    claimedMoveInDate: z.string().nullable().optional(), // ISO date string — converted to Date by route handler
    claimedNotes: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// PATCH /landlord/api/leads/:id — update prospect info / claimed values (request body)
// ---------------------------------------------------------------------------
export const UpdateLeadBodySchema = z.object({
    prospectName: z.string().nullable().optional(),
    prospectEmail: z.email().optional(),
    prospectPhone: z.string().nullable().optional(),
    source: z.string().max(100).nullable().optional(),
    claimedMonthlyIncomeCents: z.number().int().nonnegative().nullable().optional(),
    claimedHouseholdSize: z.number().int().positive().nullable().optional(),
    claimedPets: z.number().int().nonnegative().nullable().optional(),
    claimedMoveInDate: z.string().nullable().optional(), // ISO date string
    claimedNotes: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// GET /landlord/api/leads — cross-listing leads index (response + query)
// ---------------------------------------------------------------------------
export const ListLeadsResponseSchema = z.object({
    items: z.array(LeadResponseSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export const ListAllLeadsQuerySchema = z.object({
    listingId: uuid.optional(),
    // AC-8: the param must be absent (undefined) to mean "no filter" (all stages,
    // unchanged from today) vs. PRESENT-BUT-EMPTY (`?stage=`) meaning an explicit
    // empty selection (`{ in: [] }`, zero rows). A bare `v ? ... : undefined` ternary
    // would collapse both cases to `undefined` since '' is falsy — checking `v ===
    // undefined` first preserves the distinction the wire contract requires.
    stage: z
        .string()
        .optional()
        .transform((v) => (v === undefined ? undefined : v.split(',').filter(Boolean)))
        .pipe(z.array(LeadStageSchema).optional()),
    unrespondedOnly: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    since: z.string().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
    sortBy: z.enum(['recent', 'stage']).optional(),
});
// ---------------------------------------------------------------------------
// POST /landlord/api/leads/:id/messages — compose outbound reply (multipart/form-data)
// ---------------------------------------------------------------------------
export const SendReplyAttachmentSchema = z.object({
    id: uuid,
    fileName: z.string(),
    mimeType: z.string(),
    fileSize: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// POST /landlord/api/leads/:id/decide — record a final decision (request body)
// ---------------------------------------------------------------------------
// Landlord-selectable decisions only — excludes no_decision (system-only, set by the
// sibling-close path). Used by DecideLeadBodySchema so POST /decide rejects no_decision
// with 400.
export const LandlordSelectableLeadDecisionSchema = z.enum([
    'approved_lease_started',
    'withdrawn',
    'rejected',
]);
export const DecideLeadBodySchema = z.object({
    decision: LandlordSelectableLeadDecisionSchema,
    decisionNote: z.string().optional(),
    closeSiblings: z.enum(['all', 'none', 'select']).optional(),
    siblingIds: z.uuid().array().optional(),
    rejectionBasis: LeadRejectionBasisSchema.optional(),
});
// ---------------------------------------------------------------------------
// POST /landlord/api/leads/:id/rejection-notice — generate and send rejection notice
// (request body)
// ---------------------------------------------------------------------------
export const RejectionNoticeBodySchema = z.object({
    // CRA info — optional in the schema, but REQUIRED (enforced service-side) when the
    // lead's rejectionBasis = 'substantive_consumer_report', since the basis is DB-resident
    // and not present in this request body. See rejection-notice.service.ts.
    craName: z.string().optional(),
    craAddress: z.string().optional(),
    craPhone: z.string().optional(),
    // Landlord contact overrides (pre-filled from profile, editable)
    landlordAddress: z.string().optional(),
    landlordPhone: z.string().optional(),
    landlordEmail: z.string().optional(),
    // FCRA § 1681m(a)(2) credit score disclosure
    creditScoreUsed: z.boolean().optional(),
    creditScore: z.string().optional(),
    creditScoreRange: z.string().optional(),
    creditScoreDate: z.string().optional(),
    creditScoreProvider: z.string().optional(),
    creditScoreFactors: z.array(z.string()).optional(),
});
//# sourceMappingURL=leads.js.map